import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X, Pause, Play } from "lucide-react";
import { propertyFit, classFit } from "@/components/AdaptiveMediaFrame";

// Shared, viewer-facing listing photo gallery. Used on every listing-detail surface
// (owner-managed /property/:id and public/internal Marketplace /listing/:id) so the
// carousel behaviour + canonical photo data stay identical. Ownership is irrelevant here.
export const ListingGallery = ({ photos, alt = "" }) => {
  const list = photos?.length ? photos : [];
  const n = list.length;
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // drag offset (px) when zoomed
  const [galPaused, setGalPaused] = useState(false);
  const [galHover, setGalHover] = useState(false);
  const [galFocus, setGalFocus] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const thumbRefs = useRef([]);
  const touchX = useRef(null);
  const drag = useRef(null);
  const moved = useRef(false);
  const clampN = (v, mn, mx) => Math.max(mn, Math.min(mx, v));
  const [fits, setFits] = useState({}); // per-slide deterministic fit (cover/contain)
  const onSlideLoad = (i, mediaClass, e) => {
    const w = e.target?.naturalWidth, h = e.target?.naturalHeight;
    if (w && h) setFits((f) => (f[i] ? f : { ...f, [i]: classFit(mediaClass, w, h) }));
  };

  useEffect(() => { setIdx(0); }, [n]);
  useEffect(() => { setZoomed(false); }, [idx, full]);
  useEffect(() => { setPan({ x: 0, y: 0 }); }, [zoomed, idx, full]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches); on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  useEffect(() => {
    const on = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);

  const galAuto = n > 1 && !galPaused && !galHover && !galFocus && pageVisible && !reduced && !full;
  useEffect(() => {
    if (!galAuto) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % n), 5000);
    return () => clearTimeout(t);
  }, [idx, galAuto, n]);
  useEffect(() => {
    const el = thumbRefs.current[idx];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", inline: "nearest", block: "nearest" });
  }, [idx, reduced]);

  if (!n) return null;
  const cur = list[Math.min(idx, n - 1)];
  const go = (d) => setIdx((i) => (i + d + n) % n);

  return (
    <div className="min-w-0">
      <div
        data-testid="gallery"
        className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-[16/10]"
        onMouseEnter={() => setGalHover(true)}
        onMouseLeave={() => setGalHover(false)}
        onFocus={() => setGalFocus(true)}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setGalFocus(false); }}
        onKeyDown={(e) => { if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); } else if (e.key === "ArrowRight") { e.preventDefault(); go(1); } }}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); touchX.current = null; }}
      >
        <div className="flex h-full" style={{ transform: `translateX(-${idx * 100}%)`, transition: reduced ? "none" : "transform 400ms cubic-bezier(0.4,0,0.2,1)" }}>
          {list.map((ph, i) => {
            const fit = "contain"; // V3: detail gallery always shows the complete image (no crop)
            return (
              <div key={ph.id} className="relative h-full w-full shrink-0 overflow-hidden bg-slate-100" style={{ minWidth: "100%" }} aria-hidden={i !== idx}>
                {fit === "contain" && (
                  <img src={ph.url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-20 pointer-events-none" />
                )}
                <img src={ph.url} alt={alt} onLoad={(e) => onSlideLoad(i, ph.media_class, e)} className={`relative h-full w-full ${fit === "cover" ? "object-cover" : "object-contain"}`} fetchPriority={i === 0 ? "high" : "auto"} decoding="async" loading={i === 0 ? "eager" : "lazy"} />
              </div>
            );
          })}
        </div>
        {n > 1 && (<>
          <button data-testid="gallery-prev" aria-label="Previous photo" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
          <button data-testid="gallery-next" aria-label="Next photo" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
          <button data-testid="gallery-toggle" aria-label={galPaused ? "Resume automatic slideshow" : "Pause automatic slideshow"} aria-pressed={galPaused} onClick={(e) => { setGalPaused((v) => !v); e.currentTarget.blur(); }} className="absolute bottom-3 right-14 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center hover:bg-white text-navy">{galPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}</button>
        </>)}
        <button data-testid="open-fullscreen" aria-label="View fullscreen" onClick={() => setFull(true)} className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center"><Maximize2 className="h-4 w-4" /></button>
        <span className="absolute bottom-3 left-3 text-xs font-medium bg-[#0A1B33]/60 text-white px-2 py-1 rounded-full">{idx + 1} / {n}</span>
      </div>
      {n > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pc-scroll" data-testid="thumb-strip">
          {list.map((ph, i) => (
            <button key={ph.id} ref={(el) => { thumbRefs.current[i] = el; }} onClick={() => setIdx(i)} aria-label={`Photo ${i + 1}`} aria-current={i === idx} className={`h-16 w-24 rounded-lg overflow-hidden shrink-0 border-2 ${i === idx ? "border-accent" : "border-transparent"}`}><img src={ph.thumb_url || ph.url} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" /></button>
          ))}
        </div>
      )}

      {full && (
        <div className="fixed inset-0 z-[100] bg-[#0A1B33]/95 flex items-center justify-center" onClick={() => setFull(false)} data-testid="fullscreen-viewer">
          <button data-testid="close-fullscreen" onClick={(e) => { e.stopPropagation(); setFull(false); }} className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"><X className="h-5 w-5" /></button>
          <img
            src={cur.url}
            alt={alt}
            data-testid="fullscreen-image"
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => { e.stopPropagation(); drag.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y }; moved.current = false; try { e.currentTarget.setPointerCapture(e.pointerId); } catch {} }}
            onPointerMove={(e) => {
              if (!drag.current) return;
              const dx = e.clientX - drag.current.sx, dy = e.clientY - drag.current.sy;
              if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved.current = true;
              if (zoomed) {
                const maxX = window.innerWidth * 0.42, maxY = window.innerHeight * 0.42;
                setPan({ x: clampN(drag.current.px + dx, -maxX, maxX), y: clampN(drag.current.py + dy, -maxY, maxY) });
              }
            }}
            onPointerUp={(e) => { e.stopPropagation(); if (!moved.current) setZoomed((z) => !z); drag.current = null; }}
            onPointerCancel={() => { drag.current = null; }}
            style={zoomed
              ? { transform: `translate(${pan.x}px, ${pan.y}px) scale(1.8)`, transition: drag.current ? "none" : "transform 200ms ease-out", touchAction: "none" }
              : undefined}
            className={`select-none max-h-[90vh] max-w-[92vw] object-contain ${zoomed ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in transition-transform duration-300"}`}
          />
          {n > 1 && (<>
            <button data-testid="fullscreen-prev" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-4 z-10 h-12 w-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>
            <button data-testid="fullscreen-next" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-4 z-10 h-12 w-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>
          </>)}
        </div>
      )}
    </div>
  );
};
